import { useEffect, useState, useRef } from "react";
import { api, mediaUrl } from "../../lib/api";
import { Plus, Search, Edit3, Trash2, ExternalLink, Star, Loader2, X, Upload, Image as ImageIcon, FileText, Check } from "lucide-react";

const STATUS_LABEL = { active: "Active", hidden: "Hidden" };

export default function AdminMembers() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | member

  const load = async () => {
    setLoading(true);
    const r = await api.get("/admin/members");
    setItems(r.data.members);
    setCategories(r.data.categories);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (m) => {
    if (!window.confirm(`Delete ${m.name}?`)) return;
    await api.delete(`/admin/members/${m.member_id}`);
    load();
  };

  const toggleFeaturedHome = async (m, next) => {
    // Optimistic — the list shows the change immediately while the request flies
    setItems((cur) => cur.map((it) => (it.member_id === m.member_id ? { ...it, featured_home: next } : it)));
    try {
      await api.patch(`/admin/members/${m.member_id}`, { featured_home: next });
    } catch (e) {
      setItems((cur) => cur.map((it) => (it.member_id === m.member_id ? { ...it, featured_home: !next } : it)));
      window.alert(e?.response?.data?.detail || "Failed to update");
    }
  };

  const filtered = items.filter((m) => {
    if (filterCat && m.category !== filterCat) return false;
    if (q && !m.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div data-testid="admin-members-page">
      <header className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-champagne mb-2">Directory</p>
          <h1 className="font-serif text-3xl text-aubergine">Members</h1>
          <p className="mt-2 text-sm text-aubergine/65">ERC-3643 ecosystem directory.</p>
        </div>
        <button onClick={() => setEditing({})} data-testid="admin-new-member-button" className="inline-flex items-center gap-2 rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-4 py-2.5 font-mono text-xs tracking-[0.04em]">
          <Plus className="h-3.5 w-3.5" /> Add member
        </button>
      </header>

      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-aubergine/40" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search members…"
            data-testid="admin-members-search"
            className="w-full rounded-md border border-aubergine/15 bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-champagne"
          />
        </div>
        <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="admin-members-filter-category" className="rounded-md border border-aubergine/15 bg-white px-3 py-2 text-sm outline-none focus:border-champagne">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="rounded-lg border border-aubergine/10 bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-members-table">
          <thead className="bg-bone/60">
            <tr className="text-left text-[11px] font-mono uppercase tracking-[0.12em] text-aubergine/55">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3 hidden md:table-cell">Category</th>
              <th className="px-4 py-3 hidden lg:table-cell">Website</th>
              <th className="px-4 py-3 text-center" title="Show in the Home page partners carousel">Home</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-aubergine/55">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="6" className="px-4 py-12 text-center text-aubergine/55" data-testid="admin-members-empty">
                No members yet. <button onClick={() => setEditing({})} className="text-champagne hover:underline">Add the first one →</button>
              </td></tr>
            ) : filtered.map((m) => (
              <tr
                key={m.member_id}
                onClick={() => setEditing(m)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setEditing(m); } }}
                className="border-t border-aubergine/10 hover:bg-bone/40 cursor-pointer transition-colors"
                data-testid={`admin-member-row-${m.member_id}`}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {m.logo_media_id ? (
                      <img src={mediaUrl(m.logo_media_id)} alt={m.name} className="h-7 w-7 rounded object-contain bg-bone" />
                    ) : (
                      <div className="h-7 w-7 rounded bg-bone/80 flex items-center justify-center text-[10px] font-mono text-aubergine/50">{m.name.slice(0, 2).toUpperCase()}</div>
                    )}
                    <div className="font-medium text-aubergine text-left">
                      {m.name}
                      {m.member_status === "Anchor Partner" && (
                        <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-champagne/15 text-aubergine px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider">
                          <Star className="h-2.5 w-2.5 text-champagne fill-champagne" /> Anchor
                        </span>
                      )}
                      {m.member_status === "Builder" && (
                        <span className="ml-2 inline-flex items-center rounded-full bg-aubergine/8 text-aubergine/75 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider">
                          Builder
                        </span>
                      )}
                      {m.case_study_slug && m.case_study_title && (
                        <span
                          title={`Case study: ${m.case_study_title}${m.case_study_status === "draft" ? " (draft)" : ""}`}
                          className="inline-flex items-center ml-1.5 text-aubergine/55"
                          data-testid={`admin-member-case-study-indicator-${m.member_id}`}
                        >
                          <FileText className="h-3 w-3" />
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-aubergine/65">{m.category}</span>
                </td>
                <td className="px-4 py-3 hidden lg:table-cell" onClick={(e) => e.stopPropagation()}>
                  {m.website_url ? (
                    <a href={m.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-aubergine/65 hover:text-champagne text-xs truncate max-w-[200px]">
                      {m.website_url.replace(/^https?:\/\//, "")}
                      <ExternalLink className="h-3 w-3 flex-shrink-0" />
                    </a>
                  ) : <span className="text-aubergine/30 text-xs">—</span>}
                </td>
                <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                  <label className="inline-flex items-center justify-center cursor-pointer" title={m.featured_home ? "Featured on Home — click to remove" : "Click to feature on Home carousel"}>
                    <input
                      type="checkbox"
                      checked={!!m.featured_home}
                      onChange={(e) => toggleFeaturedHome(m, e.target.checked)}
                      data-testid={`admin-member-featured-home-${m.member_id}`}
                      className="accent-champagne h-4 w-4 cursor-pointer"
                    />
                  </label>
                </td>
                <td className="px-4 py-3">
                  {m.status === "active" ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-champagne/15 text-aubergine px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider"><span className="h-1.5 w-1.5 rounded-full bg-champagne" /> {STATUS_LABEL[m.status]}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-aubergine/10 text-aubergine/65 px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider"><span className="h-1.5 w-1.5 rounded-full bg-aubergine/40" /> {STATUS_LABEL[m.status]}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="inline-flex items-center gap-2">
                    <button onClick={() => setEditing(m)} data-testid={`admin-member-edit-${m.member_id}`} className="text-aubergine/55 hover:text-champagne" title="Edit"><Edit3 className="h-3.5 w-3.5" /></button>
                    <button onClick={() => onDelete(m)} data-testid={`admin-member-delete-${m.member_id}`} className="text-aubergine/55 hover:text-signal-red" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing !== null && (
        <MemberEditor
          member={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onRefresh={load}
        />
      )}
    </div>
  );
}

function MemberEditor({ member, categories, onClose, onSaved, onRefresh }) {
  const isNew = !member.member_id;
  const [form, setForm] = useState({
    name: member.name || "",
    category: member.category || categories[0] || "Infrastructure",
    member_status: member.member_status || "",
    description: member.description || "",
    website_url: member.website_url || "",
    logo_media_id: member.logo_media_id || "",
    case_study_slug: member.case_study_slug || "",
    featured: !!member.featured,
    featured_home: !!member.featured_home,
    sort_order: member.sort_order || 0,
    status: member.status || "active",
  });
  const [articles, setArticles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [, setNowTick] = useState(0); // forces re-render every second so the "Xs ago" label ticks
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/admin/articles").then((r) => setArticles(r.data.articles || [])).catch(() => setArticles([]));
  }, []);

  // Tick once a second while a "last saved" timestamp is visible. We stop the
  // interval after 10 minutes so an idle editor doesn't keep re-rendering.
  useEffect(() => {
    if (!lastSavedAt) return undefined;
    const id = setInterval(() => {
      const ageMs = Date.now() - lastSavedAt.getTime();
      if (ageMs > 10 * 60 * 1000) {
        clearInterval(id);
        return;
      }
      setNowTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(id);
  }, [lastSavedAt]);

  const onChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "members");
      const r = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, logo_media_id: r.data.media_id }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Logo upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        sort_order: Number(form.sort_order) || 0,
        // `featured` is the boolean that pins a member to the Anchor Partners
        // section on /ecosystem. It is derived ENTIRELY from the member tag
        // so that switching the tag back to "None" or "Builder" clears the
        // pin (otherwise `featured` would stay `true` from when the member
        // was last saved as an Anchor Partner — that was the symptom the
        // user reported: removing the tag still showed them as Anchor).
        featured: form.member_status === "Anchor Partner",
      };
      if (isNew) {
        await api.post("/admin/members", payload);
        onSaved();
      } else {
        const r = await api.put(`/admin/members/${member.member_id}`, payload);
        // Reconcile the form with the server's recomputed values (especially
        // `featured`, which is enforced server-side) so the editor reflects
        // the persisted state — important verification that the save landed.
        if (r?.data) {
          setForm((p) => ({ ...p, featured: !!r.data.featured, featured_home: !!r.data.featured_home }));
        }
        setLastSavedAt(new Date());
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const savedAgoLabel = (() => {
    if (!lastSavedAt) return null;
    const s = Math.floor((Date.now() - lastSavedAt.getTime()) / 1000);
    if (s < 5) return "just now";
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    return `${Math.floor(m / 60)}h ago`;
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-aubergine/40 backdrop-blur-sm" data-testid="member-editor-dialog">
      <div className="w-full max-w-2xl rounded-lg bg-white border border-aubergine/15 shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
        <header className="flex items-center justify-between px-5 py-3 border-b border-aubergine/10 bg-bone/60 flex-shrink-0">
          <h3 className="font-serif text-lg text-aubergine">
            {isNew ? "New " : "Edit "}<em className="accent-italic">member</em>
          </h3>
          <button onClick={onClose} data-testid="member-editor-close" className="p-1 rounded hover:bg-aubergine/5 text-aubergine/55"><X className="h-4 w-4" /></button>
        </header>

        <form onSubmit={save} className="p-5 space-y-4 overflow-y-auto flex-1">
          {error && <div className="rounded-md border border-signal-red/40 bg-signal-red/10 px-4 py-2 text-sm text-signal-red" data-testid="member-editor-error">{error}</div>}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-1">
              <label className="block text-xs font-medium text-aubergine mb-1.5">Logo</label>
              <div className="aspect-square rounded-md border border-aubergine/15 bg-bone/40 flex items-center justify-center overflow-hidden">
                {form.logo_media_id ? (
                  <img src={mediaUrl(form.logo_media_id)} alt="" className="max-w-full max-h-full object-contain p-3" />
                ) : (
                  <ImageIcon className="h-8 w-8 text-aubergine/25" />
                )}
              </div>
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="member-editor-logo-upload" className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-aubergine/15 hover:bg-aubergine/5 px-2 py-1.5 font-mono text-[10px] tracking-wider text-aubergine/70 disabled:opacity-60">
                {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                {form.logo_media_id ? "Replace" : "Upload"}
              </button>
              {form.logo_media_id && (
                <button type="button" onClick={() => onChange("logo_media_id", "")} data-testid="member-editor-logo-remove" className="mt-1 w-full text-[10px] text-aubergine/55 hover:text-signal-red font-mono uppercase tracking-wider">
                  Remove
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={onLogoUpload} className="hidden" />
            </div>

            <div className="sm:col-span-2 space-y-3">
              <Field label="Name" required>
                <input data-testid="member-editor-name" value={form.name} onChange={(e) => onChange("name", e.target.value)} required className={inputCls} placeholder="e.g. Apex Group" />
              </Field>
              <Field label="Category">
                <select data-testid="member-editor-category" value={form.category} onChange={(e) => onChange("category", e.target.value)} className={inputCls}>
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Website URL">
                <input data-testid="member-editor-website" type="url" value={form.website_url} onChange={(e) => onChange("website_url", e.target.value)} className={inputCls} placeholder="https://example.com" />
              </Field>
            </div>
          </div>

          <Field label="Description" hint="Short 1-2 sentence description shown on the public directory.">
            <textarea data-testid="member-editor-description" value={form.description} onChange={(e) => onChange("description", e.target.value)} rows={3} className={`${inputCls} resize-none`} />
          </Field>

          <Field label="Case-study article" hint="Optional. Pick a published article to surface as 'Read case study →' on this member's card.">
            <select
              data-testid="member-editor-case-study"
              value={form.case_study_slug}
              onChange={(e) => onChange("case_study_slug", e.target.value)}
              className={inputCls}
            >
              <option value="">— None —</option>
              {articles.map((a) => (
                <option key={a.article_id} value={a.slug} disabled={a.status !== "published"}>
                  {a.status === "published" ? a.title : `${a.title} (draft)`}
                </option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Sort order" hint="Lower = first.">
              <input data-testid="member-editor-sort-order" type="number" value={form.sort_order} onChange={(e) => onChange("sort_order", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Member tag" hint="Optional. Anchor partners pin to the top of /ecosystem.">
              <select data-testid="member-editor-member-status" value={form.member_status} onChange={(e) => onChange("member_status", e.target.value)} className={inputCls}>
                <option value="">— None —</option>
                <option value="Builder">Builder</option>
                <option value="Anchor Partner">Anchor Partner</option>
              </select>
            </Field>
            <Field label="Visibility">
              <select data-testid="member-editor-status" value={form.status} onChange={(e) => onChange("status", e.target.value)} className={inputCls}>
                <option value="active">Active</option>
                <option value="hidden">Hidden</option>
              </select>
            </Field>
          </div>
        </form>

        <footer className="flex items-center justify-end gap-3 px-5 py-3 border-t border-aubergine/10 bg-bone/40 flex-shrink-0">
          {savedAgoLabel && (
            <span
              data-testid="member-editor-last-saved"
              className="mr-auto inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.04em] text-aubergine/60"
              title={`Saved at ${lastSavedAt.toLocaleTimeString()}`}
            >
              <Check className="h-3 w-3 text-champagne" />
              Saved &middot; {savedAgoLabel}
            </span>
          )}
          <button onClick={onClose} data-testid="member-editor-cancel" className="rounded-md border border-aubergine/20 hover:bg-aubergine/5 px-3 py-2 font-mono text-xs text-aubergine">{lastSavedAt ? "Close" : "Cancel"}</button>
          <button onClick={save} disabled={saving} data-testid="member-editor-save" className="inline-flex items-center gap-2 rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-4 py-2 font-mono text-xs disabled:opacity-60">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {isNew ? "Create" : "Save changes"}
          </button>
        </footer>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-aubergine/15 bg-white px-3 py-2 text-sm outline-none focus:border-champagne transition-colors";

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-aubergine mb-1.5">
        {label} {required && <span className="text-aubergine/45 font-normal">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-[11px] text-aubergine/55">{hint}</p>}
    </div>
  );
}
